import { Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';

import { AddressInput } from '@/components/Form/AddressInput/AddressInput';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { Divider } from '@/components/Layout/Divider';
import { UpdatePersonalInfoDetailCard } from '@/components/Layout/Drawer/Drawer.styled';

import {
  COUNTRIES_WITH_POPULAR,
  NATIONALITIES_WITH_POPULAR,
} from '@/constants/countries';
import { PHONE_PATTERN } from '@/constants/patterns';
import { ADDRESS_MAX_LENGTH } from '@/constants/places';

import { useAppSelector } from '@/hooks/redux';
import { useAddressCityList } from '@/hooks/useAddressCityList';
import { useTranslate } from '@/hooks/useTranslate';

import {
  AddressCityField,
  CountryOfResidenceField,
  MailingAddressSection,
  TaxResidencySection,
} from '@/pages/onboarding/address-information/components';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import {
  selectAdobeSignFormFeature,
  selectMailingAddressFeature,
} from '@/redux/configs/selectors';
import { selectTerritories } from '@/redux/countries/selectors';

import { CountryISO } from '@/types/places';
import { User } from '@/types/user';

import { isRemediationCustomer, isTrinidadOrTobagoUser } from '@/utils/user';

import { UpdatePersonalInfo } from '../types';
import { SSNTINFields } from './SSNTINFields';

type AutoCompleteAddress = {
  city: string;
  state: string;
  zip: string;
  country: string;
  line1: string;
};

export const UpdatePersonalInfoForm = () => {
  const { phoneCodes } = useAppSelector(selectTerritories);

  const user = useAppSelector(selectLoggedInUser) as User;
  const shouldAddMailingAddressConfig = useAppSelector(
    selectMailingAddressFeature,
  );
  const isAdobeSignFormEnabled = useAppSelector(selectAdobeSignFormFeature);
  const isEnhancedTaxSectionVisible = !isAdobeSignFormEnabled;
  const isRemediation = isRemediationCustomer(user);
  const shouldShowMailingAddress =
    shouldAddMailingAddressConfig && !isRemediation;
  const shouldShowCountryOfResidence = shouldAddMailingAddressConfig;
  const { t, country: translatedCountry, nationality } = useTranslate();
  const {
    control,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { defaultValues, errors: visibleErrors },
  } = useFormContext<UpdatePersonalInfo>();

  const line1: string | AutoCompleteAddress =
    watch('addressDetailed.line1') || '';
  const differentTaxResidency = watch('differentTaxResidency');
  const country = watch('addressDetailed.country');
  const citizenship = watch('citizenship');
  const isCountryDisabledForTTCitizen =
    isTrinidadOrTobagoUser(user) &&
    citizenship?.toUpperCase() === CountryISO.Trinidad;

  const { resolveCityFromPlace } = useAddressCityList();

  const popularCountryOptions = COUNTRIES_WITH_POPULAR.map(item => ({
    ...item,
    text: translatedCountry[item.value],
  }));

  const nationalityOptions = useMemo(
    () =>
      NATIONALITIES_WITH_POPULAR.map(item => ({
        ...item,
        text: nationality[item.value],
      })),
    [nationality],
  );

  useEffect(() => {
    if (typeof line1 === 'object') {
      const autoCompleteAddress: AutoCompleteAddress = {
        ...(line1 as AutoCompleteAddress),
      };
      const { addressDetailed } = defaultValues as UpdatePersonalInfo;
      const effectiveCountry = isCountryDisabledForTTCitizen
        ? country
        : autoCompleteAddress.country;
      const line1City = resolveCityFromPlace(
        autoCompleteAddress.city,
        effectiveCountry,
      );
      setValue('addressDetailed.city', line1City, {
        shouldDirty: addressDetailed.city !== line1City,
      });
      setValue('addressDetailed.state', autoCompleteAddress.state || '', {
        shouldDirty: addressDetailed.state !== autoCompleteAddress.state,
      });
      setValue('addressDetailed.zip', autoCompleteAddress.zip || '', {
        shouldDirty: addressDetailed.zip !== autoCompleteAddress.zip,
      });
      if (!isCountryDisabledForTTCitizen) {
        setValue('addressDetailed.country', autoCompleteAddress.country || '', {
          shouldDirty: addressDetailed.country !== autoCompleteAddress.country,
        });
      }
      setValue('addressDetailed.line1', autoCompleteAddress.line1 || '', {
        shouldDirty: addressDetailed.line1 !== autoCompleteAddress.line1,
      });
      clearErrors([
        'addressDetailed.city',
        'addressDetailed.state',
        'addressDetailed.zip',
        'addressDetailed.country',
        'addressDetailed.line1',
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line1]);

  useEffect(() => {
    if (!isEnhancedTaxSectionVisible && !differentTaxResidency) {
      const country = getValues('addressDetailed.country');
      setValue('taxResidences', [country], { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [differentTaxResidency, country, isEnhancedTaxSectionVisible]);

  useEffect(() => {
    if (isCountryDisabledForTTCitizen && country !== CountryISO.Trinidad) {
      setValue('addressDetailed.country', CountryISO.Trinidad, {
        shouldDirty: true,
      });
      setValue('addressDetailed.city', '', { shouldDirty: true });
    }
  }, [country, setValue, isCountryDisabledForTTCitizen]);

  const onCountryChange = () =>
    setValue('addressDetailed.city', '', { shouldDirty: true });

  return (
    <UpdatePersonalInfoDetailCard direction={'column'}>
      <Stack direction={'column'} alignItems={'flex-start'}>
        <Typography variant="h5">
          {t('updatePersonalInfo.personalInfoForm.title')}
        </Typography>
        <Divider width="100%" />
      </Stack>
      <Stack direction="column" gap={1} alignItems="stretch">
        <Typography variant="caption">
          {t('updatePersonalInfo.personalInfoForm.field.phone.label')}
        </Typography>
        <Grid container columnSpacing={1.5} rowSpacing={2}>
          <Grid item xs={5}>
            <Select
              options={phoneCodes}
              control={control}
              name="countryCode"
              fullWidth
            />
          </Grid>
          <Grid item xs={7}>
            <TextInput
              type="tel"
              inputMode="tel"
              name="phoneNumber"
              placeholder={t(
                'updatePersonalInfo.personalInfoForm.field.phone.placeholder',
              )}
              control={control}
              error={!!visibleErrors.phoneNumber}
              helperText={visibleErrors.phoneNumber?.message}
              inputProps={{ maxLength: 7 }}
              rules={{
                required: t(
                  'updatePersonalInfo.personalInfoForm.field.phone.error.required',
                ),
                pattern: {
                  value: new RegExp(PHONE_PATTERN),
                  message: t(
                    'updatePersonalInfo.personalInfoForm.field.phone.error.pattern',
                  ),
                },
              }}
            />
          </Grid>
        </Grid>
        <Typography variant="caption">
          {t('updatePersonalInfo.personalInfoForm.field.citizenship.label')}
        </Typography>
        <Select
          options={nationalityOptions}
          control={control}
          value={citizenship}
          name="citizenship"
          error={!!visibleErrors.citizenship}
          helperText={visibleErrors.citizenship?.message}
          rules={{
            required: t(
              'updatePersonalInfo.personalInfoForm.field.citizenship.error.required',
            ),
          }}
          fullWidth
          placeholder={t(
            'updatePersonalInfo.personalInfoForm.field.citizenship.placeholder',
          )}
          // TODO: Check why MUI Select doesn't rerender when value changes on mobile
          // useDrawerOnMobile
          withSearchBar
        />
        <Divider
          width="100%"
          containerProps={{
            my: 2,
          }}
        />
        {isRemediation && (
          <Typography variant="bodyB2Regular">
            {t('reviewPersonalDetails.contactDetails.field.permanentAddress')}
          </Typography>
        )}
        {shouldShowMailingAddress && (
          <Typography
            variant="bodyB2Regular"
            mt={1}
            mb={1}
            textAlign={'center'}
          >
            <Trans
              // TODO: Update key to correct module
              i18nKey="moreInfo.reviewPersonalInfo.drawer.updatePersonalInfo.personalInfoForm.permanentAddress.heading"
              components={{ bold: <strong /> }}
            />
          </Typography>
        )}
        <Typography variant="caption">
          {t('updatePersonalInfo.personalInfoForm.field.address.label')}
        </Typography>
        <AddressInput
          name="addressDetailed.line1"
          control={control}
          textFieldProps={{
            error: !!visibleErrors.addressDetailed?.line1,
            helperText:
              visibleErrors.addressDetailed?.line1?.message ||
              (typeof line1 === 'string' &&
                line1.length >= ADDRESS_MAX_LENGTH.line1 &&
                t(
                  'updatePersonalInfo.personalInfoForm.field.address.error.maximum',
                  {
                    value: `${ADDRESS_MAX_LENGTH.line1}`,
                  },
                )),
            inputProps: { maxLength: ADDRESS_MAX_LENGTH.line1 },
          }}
          rules={{
            required: t(
              'updatePersonalInfo.personalInfoForm.field.address.error.required',
            ),
          }}
        />
        <Typography variant="caption">
          {t('updatePersonalInfo.personalInfoForm.field.city.label')}
        </Typography>
        <AddressCityField
          name="addressDetailed.city"
          control={control}
          isPending={false}
          userTerritoryCode={user.territoryCode}
          selectedCountry={country}
          hideLabel
        />

        <Typography variant="caption">
          {t('updatePersonalInfo.personalInfoForm.field.state.label')}
        </Typography>
        <Controller
          name="addressDetailed.state"
          control={control}
          rules={{
            maxLength: {
              value: ADDRESS_MAX_LENGTH.state,
              message: t(
                'updatePersonalInfo.personalInfoForm.field.state.error.maximum',
                {
                  value: `${ADDRESS_MAX_LENGTH.state}`,
                },
              ),
            },
          }}
          render={({
            fieldState: { error },
            field: { value: fieldValue, onChange },
          }) => {
            return (
              <TextInput
                name="addressDetailed.state"
                value={fieldValue}
                onChange={onChange}
                error={!!error}
                helperText={
                  error?.message ||
                  (fieldValue.length >= ADDRESS_MAX_LENGTH.state &&
                    t(
                      'updatePersonalInfo.personalInfoForm.field.state.error.maximum',
                      {
                        value: `${ADDRESS_MAX_LENGTH.state}`,
                      },
                    ))
                }
                fullWidth
                inputProps={{
                  maxLength: ADDRESS_MAX_LENGTH.state,
                }}
              />
            );
          }}
        />
        <Typography variant="caption">
          {t('updatePersonalInfo.personalInfoForm.field.zipCode.label')}
        </Typography>
        <Controller
          name="addressDetailed.zip"
          control={control}
          render={({
            fieldState: { error },
            field: { value: fieldValue, onChange },
          }) => {
            return (
              <TextInput
                name="addressDetailed.zip"
                error={!!error}
                helperText={
                  error?.message ||
                  (fieldValue.length >= ADDRESS_MAX_LENGTH.zip &&
                    t(
                      'updatePersonalInfo.personalInfoForm.field.zipCode.error.maximum',
                      {
                        value: ADDRESS_MAX_LENGTH.zip,
                      },
                    ))
                }
                fullWidth
                onChange={onChange}
                value={fieldValue}
                inputProps={{
                  maxLength: ADDRESS_MAX_LENGTH.zip,
                }}
              />
            );
          }}
          rules={{
            maxLength: {
              value: ADDRESS_MAX_LENGTH.zip,
              message: t(
                'updatePersonalInfo.personalInfoForm.field.zipCode.error.maximum',
                {
                  value: ADDRESS_MAX_LENGTH.zip,
                },
              ),
            },
          }}
        />
        <Typography variant="caption">
          {t('updatePersonalInfo.personalInfoForm.field.country.label')}
        </Typography>
        <Select
          options={popularCountryOptions}
          control={control}
          name="addressDetailed.country"
          error={!!visibleErrors.addressDetailed?.country}
          helperText={visibleErrors.addressDetailed?.country?.message}
          rules={{
            required: t(
              'updatePersonalInfo.personalInfoForm.field.country.error.required',
            ),
          }}
          fullWidth
          placeholder={t(
            'updatePersonalInfo.personalInfoForm.field.country.placeholder',
          )}
          useDrawerOnMobile
          withSearchBar
          disabled={isCountryDisabledForTTCitizen}
          style={{ marginTop: 2 }}
          onSelectChange={onCountryChange}
        />
        {shouldShowMailingAddress && (
          <Stack direction="column" width="100%" mt={1}>
            <MailingAddressSection
              popularCountryOptions={popularCountryOptions}
            />
          </Stack>
        )}
        {shouldShowCountryOfResidence && (
          <>
            <Divider containerProps={{ my: 2 }} width={'100%'} />
            <Typography variant="caption">
              {t('createNewAccount.addressPage.form.residenceCountry.label')}
            </Typography>
            <CountryOfResidenceField
              hideLabel
              popularCountryOptions={popularCountryOptions}
            />
          </>
        )}
        {!isEnhancedTaxSectionVisible && (
          <>
            <TaxResidencySection
              control={control}
              popularCountryOptions={popularCountryOptions}
              taxResidencesError={!!visibleErrors.taxResidences}
              taxResidencesHelperText={visibleErrors.taxResidences?.message}
              labels={{
                toggleLabel: t(
                  'updatePersonalInfo.personalInfoForm.field.tax.label',
                ),
                countryLabel: t(
                  'updatePersonalInfo.personalInfoForm.field.countryOfTax.label',
                ),
                countryPlaceholder: t(
                  'updatePersonalInfo.personalInfoForm.field.countryOfTax.placeHolder',
                ),
                countryRequiredError: t(
                  'updatePersonalInfo.personalInfoForm.field.countryOfTax.error.required',
                ),
              }}
            />
            <SSNTINFields
              user={user}
              differentTaxResidency={differentTaxResidency}
            />
          </>
        )}
      </Stack>
    </UpdatePersonalInfoDetailCard>
  );
};

export default React.memo(UpdatePersonalInfoForm);
